import type { JobSalary, MetroArea, Quantile } from "@lossdog/job-watchlists";
import { PillButton, Typography } from "@povio/ui";
import { useSignals } from "@preact/signals-react/runtime";

import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import type { TimePeriod } from "@/components/watchlist/charts/HistoricalSalaryChart";
import { HistoricalSalaryChart } from "@/components/watchlist/charts/HistoricalSalaryChart";
import JobFitSection from "@/components/watchlist/components/JobFitSection";
import { useHistoricalSalaryData } from "@/components/watchlist/hooks/useHistoricalSalaryData";
import { JobSalaryLastPrice } from "@/components/watchlist/job-salary-ticker/JobSalaryLastPrice";
import {
  JobSalaryNetChange,
  JobSalaryNetChangeIcon,
} from "@/components/watchlist/job-salary-ticker/JobSalaryNetChange";
import { SimilarJobPositionsTable } from "@/components/watchlist/SimilarJobPositionsTable";
import type { WatchlistModels } from "@/data/watchlist/watchlist.models";
import { NumberUtils } from "@/util/number.utils";

interface JobDetailsProps {
  selectedJobSalary: JobSalary | null;
  selectedMetroArea: MetroArea | null;
  quantiles: Quantile[] | undefined;
  selectedQuantile: Quantile | null;
  setSelectedQuantile: (quantile: Quantile) => void;
  jobFitData: WatchlistModels.WatchlistJobFitDto | null;
  preloadedJobFit?: { fitScore: number; status: string } | null;
  onCalculateJobFit: () => void;
  isLoadingJobFit: boolean;
  relatedJobSalaries: JobSalary[];
  setSelectedJobSalary: (jobSalary: JobSalary) => void;
  isDemoModeOrShowDemoData: boolean;
}

export const JobDetails = ({
  selectedJobSalary,
  jobFitData,
  preloadedJobFit,
  onCalculateJobFit,
  isLoadingJobFit,
  selectedQuantile,
  selectedMetroArea,
  quantiles,
  setSelectedQuantile,
  relatedJobSalaries,
  setSelectedJobSalary,
}: JobDetailsProps) => {
  return (
    <div className="flex flex-col gap-1">
      {/* Job Title and Salary */}
      <div className="flex flex-row items-center justify-start gap-4 px-2 pt-1-5 pb-1">
        <Typography
          size="title-3"
          variant="prominent-1"
          sizeMobile="title-3"
          className="text-text-default-1"
        >
          {selectedJobSalary?.title}
        </Typography>
        <Typography
          size="title-3"
          sizeMobile="title-3"
          variant="prominent-1"
        >
          <JobSalaryLastPrice jobSalary={selectedJobSalary} />
        </Typography>
        <Typography
          size="title-3"
          sizeMobile="title-3"
          variant="prominent-1"
          className="flex flex-row items-center gap-0-5"
        >
          <JobSalaryNetChange jobSalary={selectedJobSalary} />
          <JobSalaryNetChangeIcon jobSalary={selectedJobSalary} />
        </Typography>
      </div>
      <div className="flex flex-row items-center dd:px-0 px-6">
        <JobFitSection
          jobFitData={jobFitData}
          preloadedJobFit={preloadedJobFit}
          calculateJobFit={onCalculateJobFit}
          isLoadingJobFit={isLoadingJobFit}
        />
      </div>
      <RangeTable
        jobData={selectedJobSalary}
        selectedQuantile={selectedQuantile}
        timePeriod="five-year"
        selectedMetroArea={selectedMetroArea}
        quantiles={quantiles}
        setSelectedQuantile={setSelectedQuantile}
      />
      <HistoricalSalaryChart
        jobData={selectedJobSalary}
        quantile={selectedQuantile}
      />
      {relatedJobSalaries?.length > 0 && (
        <SimilarJobPositionsTable
          relatedJobSalaries={relatedJobSalaries}
          onSelectedJobSalary={(jobSalary) => {
            setSelectedJobSalary(jobSalary);
            const scrollContainer = document.getElementById("scroll-container");
            scrollContainer?.scrollTo({ top: 300, behavior: "smooth" });
          }}
        />
      )}
    </div>
  );
};

const RangeTable = ({
  jobData,
  selectedQuantile,
  timePeriod,
  selectedMetroArea,
  quantiles,
  setSelectedQuantile,
}: {
  jobData: JobSalary | null;
  selectedQuantile: Quantile | null;
  timePeriod: TimePeriod;
  selectedMetroArea: MetroArea | null;
  quantiles: Quantile[] | undefined;
  setSelectedQuantile: (quantile: Quantile) => void;
}) => {
  useSignals();
  const { minSalary, maxSalary } = useHistoricalSalaryData({
    jobSalary: jobData,
    quantile: selectedQuantile,
    timePeriod,
  });

  const baseSalaryRangeTitle = `${timePeriod === "five-year" ? "5 Year" : "1 Year"} Range`;

  if (!jobData) {
    return null;
  }

  return (
    <div className="dd:mx-0 mx-3 flex flex-col">
      <div className="flex flex-row items-center justify-between gap-2 border-elevation-outline-default-1 border-b p-4">
        <Typography
          size="label-4"
          className="text-text-default-3"
        >
          {baseSalaryRangeTitle}
        </Typography>
        <Typography
          size="label-4"
          className="text-right text-text-default-1"
        >
          {NumberUtils.formatAsUSCurrency(Math.min(jobData.salary.value, minSalary))}-
          {NumberUtils.formatAsUSCurrency(Math.max(jobData.salary.value, maxSalary))}
        </Typography>
      </div>
      <div className="flex flex-row items-center justify-between gap-2 border-elevation-outline-default-1 border-b p-4">
        <Typography
          size="label-4"
          className="text-text-default-3"
        >
          Location
        </Typography>
        <Typography
          size="label-4"
          className="text-right text-text-default-1"
        >
          {selectedMetroArea?.title}
        </Typography>
      </div>
      <div className="flex flex-row items-center justify-between gap-2 border-elevation-outline-default-1 border-b p-4">
        <Typography
          size="label-4"
          className="text-text-default-3"
        >
          Exp
        </Typography>
        <div className="flex flex-row items-center gap-2">
          {quantiles?.map((quantile) => (
            <DemoClickWrapper key={quantile.id}>
              <PillButton
                toggle
                isSelected={selectedQuantile?.id === quantile.id}
                variant="outlined"
                color="secondary"
                onPress={() => setSelectedQuantile(quantile)}
              >
                {quantile.short}
              </PillButton>
            </DemoClickWrapper>
          ))}
        </div>
      </div>
    </div>
  );
};
